import type { ReactNode } from 'react';
import { cx } from '../cx';
import { devWarn } from '../dev';
import { LineKey, NARROW_ONLY, WIDE_ONLY, type ChartLayout } from './layout';
import { spreadLabels } from './LineChart';
import { TICK_TEXT, chartStroke, niceScale, percentOf, thinTick } from './scale';

/** One ad: how many days it was up, and whether it is still open today (censored). */
export type ListingSpan = { days: number; open: boolean };

/** A survival step: from `day` on, `share` (0 to 1) of ads are still up. */
export type LifespanStep = { day: number; share: number };

/**
 * The Kaplan-Meier estimate: the share of ads still up after each day, counting ads that are still
 * open as open until the day we last saw them (so long-running ads do not vanish from the
 * average). Returns the steps, starting at day 0 with every ad up, and the days of still-open ads.
 */
export function kaplanMeier(spans: ListingSpan[]): { steps: LifespanStep[]; censored: number[] } {
  const days = [...new Set(spans.map((s) => s.days))].sort((a, b) => a - b);
  const steps: LifespanStep[] = [{ day: 0, share: 1 }];
  let share = 1;
  let atRisk = spans.length;
  for (const d of days) {
    const here = spans.filter((s) => s.days === d);
    const closed = here.filter((s) => !s.open).length;
    if (closed && atRisk) {
      share *= 1 - closed / atRisk;
      steps.push({ day: d, share });
    }
    atRisk -= here.length;
  }
  return { steps, censored: spans.filter((s) => s.open).map((s) => s.days) };
}

/** The share still up on a given day, read off the steps. */
export function shareStillUp(steps: LifespanStep[], day: number): number {
  let share = 1;
  for (const s of steps) if (s.day <= day) share = s.share;
  return share;
}

/** The first day the share still up falls to `share` or below (day 19 for "half are gone by day 19"). */
export function dayWhenShareFalls(steps: LifespanStep[], share: number): number | null {
  return steps.find((s) => s.share <= share)?.day ?? null;
}

export type LifespanSeries = {
  id: string;
  label: ReactNode;
  /** From `kaplanMeier`. */
  steps: LifespanStep[];
  /** Days of ads still open today: drawn as small ticks on the curve. */
  censored?: number[];
};

export type LifespanCurveProps = {
  /** One to four groups. */
  series: LifespanSeries[];
  highlightId?: string;
  /** The last day on the axis (90). */
  maxDay: number;
  /** The one labelled reading: the day the story group's curve falls to a share, with its words. */
  reading?: { day: number; share: number; label: ReactNode };
  formatDay: (day: number) => string;
  formatShare: (share: number) => string;
  /** `auto` (default) switches to the narrow form in a narrow container: group names and the reading move under the plot. */
  layout?: ChartLayout;
  /** One sentence with the reading, for screen readers. */
  description: string;
  className?: string;
};

const GUTTER: Record<ChartLayout, string> = { auto: 'mr-2 @md:mr-28', narrow: 'mr-2', wide: 'mr-28' };

/**
 * Listing lifespan: the share of ads still up after each day, one step curve per group, with
 * ticks for ads still open today. It is first seen to last seen, never "time to hire": ads come down
 * for many reasons. Crawl gaps must not be counted as closings.
 */
export function LifespanCurve({
  series,
  highlightId,
  maxDay,
  reading,
  formatDay,
  formatShare,
  layout = 'auto',
  description,
  className,
}: LifespanCurveProps) {
  devWarn(series.length > 4, `LifespanCurve has ${series.length} curves; four at most.`);
  const story = highlightId ?? (series.length === 1 ? series[0].id : undefined);
  const x = (d: number) => percentOf(d, 0, maxDay);
  const y = (s: number) => 100 - s * 100;
  const points = (steps: LifespanStep[]) => {
    const pts: string[] = [];
    let prev = 1;
    for (const s of steps.filter((t) => t.day <= maxDay)) {
      pts.push(`${x(s.day)},${y(prev)}`, `${x(s.day)},${y(s.share)}`);
      prev = s.share;
    }
    pts.push(`100,${y(prev)}`);
    return pts.join(' ');
  };
  const endY = series.map((s) => y(shareStillUp(s.steps, maxDay)));
  const labelY = spreadLabels(endY);
  const dayTicks = niceScale(0, maxDay, { zero: true }).ticks.filter((t) => t <= maxDay);
  return (
    <div role="img" aria-label={description} className={cx('@container flex flex-col gap-1', className)}>
      <div className="flex gap-2">
        <div className="relative h-48 w-10 shrink-0" aria-hidden="true">
          {[0, 0.5, 1].map((t) => (
            <span
              key={t}
              className={cx('absolute right-0 translate-y-1/2', TICK_TEXT)}
              style={{ bottom: `${t * 100}%` }}
            >
              {formatShare(t)}
            </span>
          ))}
        </div>
        <div className={cx('relative h-48 flex-1', GUTTER[layout])}>
          {[0, 0.5, 1].map((t) => (
            <span
              key={t}
              className="absolute inset-x-0 border-t border-jc-chart-grid"
              style={{ bottom: `${t * 100}%` }}
            />
          ))}
          <svg
            viewBox="0 0 100 100"
            preserveAspectRatio="none"
            className="absolute inset-0 size-full overflow-visible motion-safe:animate-jc-fade"
          >
            {[...series.filter((s) => s.id !== story), ...series.filter((s) => s.id === story)].map((s) => (
              <polyline
                key={s.id}
                points={points(s.steps)}
                fill="none"
                vectorEffect="non-scaling-stroke"
                strokeWidth={s.id === story ? 2.5 : 1.75}
                className={chartStroke(s.id === story ? 'subject' : 'context')}
              />
            ))}
          </svg>
          {series.flatMap((s) =>
            (s.censored ?? [])
              .filter((d) => d <= maxDay)
              .map((d, k) => (
                <span
                  key={`${s.id}-c-${k}`}
                  aria-hidden="true"
                  className={cx(
                    'absolute h-2 w-0.5 -translate-1/2',
                    s.id === story ? 'bg-jc-ink' : 'bg-jc-chart-context',
                  )}
                  style={{ left: `${x(d)}%`, top: `${y(shareStillUp(s.steps, d))}%` }}
                />
              )),
          )}
          {reading ? (
            <span
              aria-hidden="true"
              className="absolute bottom-0 border-l-2 border-dashed border-jc-ink-secondary"
              style={{ left: `${x(reading.day)}%`, top: `${y(reading.share)}%` }}
            >
              <span
                className={cx(
                  'absolute bottom-full left-1.5 mb-1 w-44 text-xs font-semibold text-jc-ink',
                  WIDE_ONLY[layout],
                )}
              >
                {reading.label}
              </span>
            </span>
          ) : null}
          {series.map((s, i) => (
            <span
              key={s.id}
              aria-hidden="true"
              className={cx(
                'absolute left-full w-28 -translate-y-1/2 truncate pl-3 text-xs',
                WIDE_ONLY[layout],
                s.id === story ? 'font-semibold text-jc-ink' : 'text-jc-ink-secondary',
              )}
              style={{ top: `${labelY[i]}%` }}
            >
              {s.label}
            </span>
          ))}
        </div>
      </div>
      <div className={cx('relative ml-12 h-4', GUTTER[layout])} aria-hidden="true">
        {dayTicks.map((t, i) => (
          <span
            key={t}
            className={cx('absolute -translate-x-1/2 whitespace-nowrap', TICK_TEXT, thinTick(i, dayTicks.length))}
            style={{ left: `${x(t)}%` }}
          >
            {formatDay(t)}
          </span>
        ))}
      </div>
      <div className={cx('flex flex-col gap-1 pt-1', NARROW_ONLY[layout])}>
        {reading ? (
          <p className="flex items-start gap-2 type-meta font-semibold text-jc-ink" aria-hidden="true">
            <span className="mt-0.5 h-4 shrink-0 border-l-2 border-dashed border-jc-ink-secondary" />
            {reading.label}
          </p>
        ) : null}
        <LineKey items={series.map((s) => ({ id: s.id, label: s.label, story: s.id === story }))} />
      </div>
    </div>
  );
}
